import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CategoryService from "../../../../services/CategoryService";
import { colors } from "../../../config/colors";

const CreateCategory = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // Get category ID from URL if editing
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({
    categoryType: "",
    description: "",
  });

  // Add validation message state
  const [validationMessage, setValidationMessage] = useState({
    categoryType: ''
  });

  useEffect(() => {
    if (id) {
      setIsEditMode(true);
      fetchCategoryData();
    }
  }, [id]);

  useEffect(() => {
    if (!id) {
      setIsEditMode(false);
      setFormData({
        categoryType: "",
        description: "",
      });
      // Optionally reset validation messages or other state here
    }
  }, [id]);

  const fetchCategoryData = async () => {
    setLoading(true);
    try {
      const response = await CategoryService.getCategoryById(id);
      if (response.data.success) {
        const categoryData = response.data.data;
        setFormData({
          categoryType: categoryData.categoryType,
          description: categoryData.description || "",
        });
      }
    } catch (error) {
      console.error("Error fetching category data:", error);
      toast.error("Failed to fetch category data");
    } finally {
      setLoading(false);
    }
  };

  const showTemporaryError = (field, message) => {
    setValidationMessage(prev => ({
      ...prev,
      [field]: message
    }));

    // Clear the message after 1 second
    setTimeout(() => {
      setValidationMessage(prev => ({
        ...prev,
        [field]: ''
      }));
    }, 2000);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'categoryType') {
      // Prevent starting with space
      if (value === ' ') {
        showTemporaryError('categoryType', 'Cannot start with space');
        return;
      }
      // Only allow letters, numbers, spaces and basic punctuation
      const sanitizedValue = value.replace(/[^a-zA-Z0-9\s.,'-]/g, '');
      if (sanitizedValue !== value) {
        showTemporaryError('categoryType', 'Special characters not allowed');
      }
      setFormData((prev) => ({
        ...prev,
        [name]: sanitizedValue,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const validateForm = () => {
    if (!formData.categoryType.trim()) {
      toast.error("Category type is required");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      let response;
      if (isEditMode) {
        response = await CategoryService.updateCategory(id, formData);
      } else {
        response = await CategoryService.createCategory(formData);
      }

      if (response.success || response?.status === 200) {
        toast.success(`Category ${isEditMode ? "updated" : "created"} successfully`);
        navigate("/category-list");
      } else {
        toast.error(response.message || `Failed to ${isEditMode ? "update" : "create"} category`);
      }
    } catch (error) {
      console.error(`Error ${isEditMode ? "updating" : "creating"} category:`, error);
      toast.error(error.response?.data?.message || `Failed to ${isEditMode ? "update" : "create"} category`);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    navigate("/category-list");
  };

  return (
    <div className="h-[calc(100vh-4rem)] overflow-y-auto bg-gradient-to-b from-white to-accent-light/10 py-4">
      {/* Floating Header */}
      <div className="sticky top-0 bg-white/80 backdrop-blur-lg border-b border-accent-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 rounded-full hover:bg-accent-light/20 transition-colors"
              >
                <svg className="w-5 h-5 text-primary-headings" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <h1 className="text-xl font-semibold text-primary-headings">
                {isEditMode ? "Edit Category" : "Create New Category"}
              </h1>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex flex-col items-center justify-center h-[60vh]">
            <div className="w-16 h-16 relative">
              <div className="w-16 h-16 rounded-full border-4 border-accent-light"></div>
              <div className="w-16 h-16 rounded-full border-4 border-primary-headings border-t-transparent animate-spin absolute top-0"></div>
            </div>
            <p className="mt-4 text-accent-dark">Creating category...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Category Details Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-accent-light/20">
              <div className="px-6 py-4 bg-gradient-to-r from-primary-light/5 to-transparent">
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5 text-primary-headings" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                  <h2 className="text-lg font-semibold text-primary-headings">Category Details</h2>
                </div>
              </div>
              
              <div className="p-6 space-y-6">
                {/* Category Type Input */}
                <div className="relative">
                  <label className="block text-sm font-medium text-accent-dark mb-2">
                    Type <span className="text-cta">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      name="categoryType"
                      value={formData.categoryType}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2.5 bg-accent-light/5 border border-accent-light/30 rounded-lg shadow-sm focus:ring-2 focus:ring-primary-light focus:border-transparent"
                      placeholder="Enter category type"
                    />
                    {validationMessage.categoryType && (
                      <p className="mt-1 text-sm text-red-500 absolute">
                        {validationMessage.categoryType}
                      </p>
                    )}
                  </div>
                </div>

                {/* Description Input */}
                {/* <div className="relative">
                  <label className="block text-sm font-medium text-accent-dark mb-2">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows="4"
                    className="w-full px-4 py-2.5 bg-accent-light/5 border border-accent-light/30 rounded-lg shadow-sm focus:ring-2 focus:ring-primary-light focus:border-transparent"
                    placeholder="Enter category description"
                  />
                </div> */}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mt-8 border-t border-accent-light/20 pt-6">
              <div className="flex justify-end items-center">
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2.5 bg-cta text-white rounded-lg hover:bg-cta/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cta transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (isEditMode ? "Updating..." : "Creating...") : (isEditMode ? "Update Category" : "Create Category")}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default CreateCategory;
